import React, { useEffect, useState } from "react";
import { instantiateDesignFromTemplate } from "api/realesyStudioApi";
import { getAuthCookie } from "../utils/authCookies";

type Template = {
  template_id: string;
  design_id: string;
  design_name?: string;
  product_id: string;
  variant_id: string;
  template_slug: string;
  category: string;
  tags: string[];
  width: number;
  height: number;
  units: string;
  orientation: string;
  preview_url: string;
  editable_rules: any;
  created_by: {
    id: string;
    name: string;
  };
  assigned_at: string;
};

type TemplateDesignsSectionProps = {
  productId: string;
  variantId: string;
  signageId: string;
};

const TemplateDesignsSection: React.FC<TemplateDesignsSectionProps> = ({
  productId,
  variantId,
  signageId,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [instantiatingDesign, setInstantiatingDesign] = useState(false);

  const token = getAuthCookie("sessionToken") || "";
  const studioBaseUrl = (process.env.REACT_APP_STUDIO_BASE_URL || "").trim().replace(/\/+$/, "");

  // Fetch the single assigned template for this variant
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!process.env.REACT_APP_API_URL || !token || !productId || !variantId) {
        setTemplates([]);
        return;
      }

      try {
        setLoadingTemplates(true);
        const url = `${process.env.REACT_APP_API_URL}/realesy-studio/product-variant-templates/datatable?product_id=${encodeURIComponent(productId)}&variant_id=${encodeURIComponent(variantId)}`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ page: 1, limit: 1 }),
        });

        if (!response.ok) throw new Error("Failed to fetch templates");

        const data = await response.json();
        setTemplates(Array.isArray(data?.data) ? data.data : []);
      } catch (err) {
        console.error("Error fetching templates:", err);
        setTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };

    if (productId && variantId) {
      fetchTemplates();
    } else {
      setTemplates([]);
    }
  }, [productId, variantId, token]);

  // Handle customize button click
  const handleCustomize = async (templateId: string) => {
    if (!templateId || !studioBaseUrl || !signageId) return;
    try {
      setInstantiatingDesign(true);
      const result = await instantiateDesignFromTemplate(templateId, signageId);
      const returnPath = `${window.location.pathname}${window.location.search || ""}`;
      const studioUrl = new URL(
        `${studioBaseUrl}/open-design/${encodeURIComponent(result.design_id)}`
      );
      studioUrl.searchParams.set("return_path", returnPath);
      window.location.assign(studioUrl.toString());
    } catch (error) {
      console.error("Failed to instantiate design:", error);
    } finally {
      setInstantiatingDesign(false);
    }
  };

  // Each variant has exactly one design; grab it (or null while loading / absent)
  const assignedTemplate = templates[0] ?? null;

  if (loadingTemplates) {
    return (
      <button
        type="button"
        className="btn btn-primary table-addbutton fw-bold fs-16"
        disabled
      >
        <span className="spinner-border spinner-border-sm me-2" />
        Loading...
      </button>
    );
  }

  if (!assignedTemplate) {
    return null;
  }

  return (
    <button
      type="button"
      className="btn btn-primary table-addbutton fw-bold fs-16 mb-3"
      disabled={instantiatingDesign}
      onClick={() => handleCustomize(assignedTemplate.template_id)}
    >
      {/* <i className="bi bi-brush me-2" /> */}
      {instantiatingDesign ? "Opening Design..." : "Customize the Design"}
    </button>
  );
};

export default TemplateDesignsSection;
